// Vue app
import { createApp } from "vue";
import App from "./App.vue";

import "@/assets/styles/main.css";
import TrackHelper from "@/helpers/utilities/TrackHelper";

import PiniaStore from "@/stores";
import { useSettingsStore } from "@/stores/settings";
import { i18n, loadLanguageAsync } from "@/helpers/utilities/I18nHelper";

// Helpers
import { router, setTranslater } from "./router";
import apiGraph from "@/helpers/api/apiGraph";
import msalConnection from "@/helpers/api/msalConnection";
import apiSkobo from "@/helpers/api/apiSkobo";

import { SessionHelper } from "@/helpers/utilities/sessionHelper";
import FontAwesomeHelper from "@/helpers/utilities/FontAwesomeHelper";

// Plugins
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { messageState as snackbarMessageState } from "@/components/Shared/GlobalSnackbar.vue";
import { registerDirectives } from "@/directives";

/**********************************************/
// TRACKER
/**********************************************/
TrackHelper.initialize();

/**
 *
 */
export async function initialize() {
  try {
    let apiTokenFromQuery;
    let apiToken;

    if (window.location.search.substring(1).startsWith("token=")) {
      apiTokenFromQuery = window.location.search.substring(1).replace("token=", "");
      apiToken = apiTokenFromQuery;
    } else {
      apiToken = SessionHelper.nctSessionToken();
    }

    if (apiToken?.length) {
      // Setup router
      SessionHelper.login(apiToken);
      apiSkobo.init(router);

      // Setup pinia
      const pinia = PiniaStore.init();

      setTranslater(i18n);

      /**********************************************/
      // FONT-AWESOME
      /**********************************************/
      FontAwesomeHelper.initFontAwesome();

      /**********************************************/
      // CREATE APP
      /**********************************************/
      const app = createApp(App);

      app.use(router);
      app.use(pinia);
      app.use(i18n);

      // Vue Router captures window.location at createRouter() time (module import), before the token
      // is stripped via pushState. Its async initial navigation would re-push /?token=... to the URL
      // when the microtask queue drains (e.g. during the first await below). Replace it now so
      // Vue Router's internal state matches the already-cleaned URL.
      if (apiTokenFromQuery) {
        await router.replace({ path: window.location.pathname });
      }

      app.component("FontAwesomeIcon", FontAwesomeIcon);
      registerDirectives(app);

      Object.defineProperty(app.config.globalProperties, "$locale", {
        get: () => i18n.global.locale.value,
        set: locale => {
          i18n.global.locale.value = locale;
        }
      });
      app.config.globalProperties.$skboMessage = snackbarMessageState;
      app.config.globalProperties.$ncProfile = { HideFileExtensions: false };

      /**********************************************/
      // SETUP GRAPH API
      /**********************************************/
      await apiGraph.initGraphApi(
        import.meta.env.VITE_APP_MSGRAPH_API_ENDPOINT,
        import.meta.env.VITE_APP_MSGRAPH_API_ENDPOINT_NEW
      );

      /**********************************************/
      // SETUP MSAL
      /**********************************************/

      const settingsStore = useSettingsStore();
      await settingsStore.loadSetup();

      await msalConnection.init(settingsStore.interfaces.tenant, settingsStore.interfaces.graphAPIClientId);

      TrackHelper.installVueErrorHandler(app);

      /**********************************************/
      // MOUNT APP
      /**********************************************/
      app.mount("#app");

      if (SessionHelper.nextLoginRoute && SessionHelper.nextLoginRoute !== window.location.pathname) {
        const pathForLoad = SessionHelper.nextLoginRoute;
        if (pathForLoad) {
          router.push({ path: pathForLoad, replace: true });
        }
      } else {
        SessionHelper.deleteNextLoginRoute();
      }

      /**********************************************/
      // Forms detection for handling if input has value
      /**********************************************/

      document.addEventListener("change", function (event) {
        if (event.target?.matches(".form-element-field")) {
          event.target.classList[event.target.value ? "add" : "remove"]("-hasvalue");
        }
      });
    } else {
      // If we have a route but is not actively logged out (eg. we do  not have a system name) redirect to kviklogin
      if (!SessionHelper.customerSystemName) {
        sendToKviklogin();
        return;
      }

      const pinia = PiniaStore.init();

      loadLanguageAsync(import.meta.env.VITE_APP_I18N_FALLBACK_LOCALE, import.meta.env.VITE_APP_I18N_FALLBACK_VARIANT);

      const app = createApp(App);
      app.use(router);
      app.use(pinia);
      app.use(i18n);
      app.mount("#app");
    }
  } catch {
    sendToKviklogin();
  }
}

initialize();

/**
 * Sends browser to kviklogin
 */
function sendToKviklogin() {
  SessionHelper.nextLoginRoute = window.location.pathname;
  if (import.meta.env.VITE_APP_NCT_LOGIN_ENDPOINT) {
    window.location.replace(import.meta.env.VITE_APP_NCT_LOGIN_ENDPOINT);
  } else {
    window.location.replace("https://kviklogin.dk");
  }
}
